import { getErrorOccurrenceId } from '@layerswap/widget-types';
import { ErrorHandler } from '../lib/ErrorHandler';
import { lifecycleErrorDetails } from './swapLifecycle';
/**
 * Request scope. The gasless flip is keyed to the API refusing the request (rejection or
 * an `error` envelope); a 2xx without an id is not a gasless refusal.
 */
export async function requestSwap({ request, useGasless }, onGaslessUnavailable) {
    let response;
    try {
        response = await request();
    }
    catch (error) {
        if (useGasless)
            onGaslessUnavailable();
        throw error;
    }
    if (response?.error) {
        if (useGasless)
            onGaslessUnavailable();
        throw response.error;
    }
    const swap = response?.data;
    if (!swap?.swap.id)
        throw new Error('Could not create swap');
    return swap;
}
/** Post-creation scope. Synchronous by contract: nothing here can reject the attempt. */
export function announceCreatedSwap(swap, effects) {
    for (const effect of effects) {
        try {
            effect.run(swap);
        }
        catch (caught) {
            const error = caught instanceof Error ? caught : new Error(String(caught));
            ErrorHandler({
                type: 'SideEffectError',
                operation: effect.name,
                swapId: swap.swap.id,
                occurrenceId: getErrorOccurrenceId(caught),
                name: error.name,
                message: error.message,
                stack: error.stack,
                cause: caught,
            });
        }
    }
}
/**
 * Attempt scope. Invariants:
 *  (a) exactly one of swap_created / swap_creation_failed per call;
 *  (b) the promise rejects iff swap_creation_failed was emitted;
 *  (c) onGaslessUnavailable fires only from requestSwap;
 *  (d) post-creation effects never affect (a)/(b).
 */
export async function createSwapAttempt(prepare, hooks) {
    const base = { stage: 'swap_creation', path: hooks.path };
    hooks.onLifecycle({ step: 'swap_creation_started', outcome: 'started', ...base, ...hooks.lifecycleContext });
    let prepared;
    let swap;
    try {
        prepared = await prepare();
        swap = await requestSwap(prepared, hooks.onGaslessUnavailable);
    }
    catch (error) {
        hooks.onLifecycle({ step: 'swap_creation_failed', outcome: 'failed', ...base, ...lifecycleErrorDetails(error), ...hooks.lifecycleContext });
        throw error;
    }
    announceCreatedSwap(swap, [
        {
            name: 'swap_created_lifecycle',
            run: () => hooks.onLifecycle({
                step: 'swap_created',
                outcome: 'succeeded',
                ...base,
                ...hooks.lifecycleContext,
                swapId: swap.swap.id,
                fromAddress: swap.swap.source_address,
                status: swap.swap.status,
            }),
        },
        ...prepared.onCreated,
    ]);
    return swap;
}
